import React, { useContext, useMemo, FC } from 'react';

import { Element, Text } from 'slate';

import styled from 'styled-components';

// context
import { EditorContext } from '../../../context/EditorContext';

// helpers
import random from '../../../helpers/random';

// models
import { ElementKind } from '../../../models/editor.model';
import { FormulaTypesData } from '../../../models/formula.model';

interface PhoneViewProps {
    className?: string;
    isCoverPreview?: boolean;
    showTestPreview?: boolean;
}

const previewMessagesDefaultCount = 5;

const PhoneView: FC<React.PropsWithChildren<PhoneViewProps>> = ({
    className,
    isCoverPreview,
    showTestPreview,
}) => {
    const {
        editorDetails: { previews, testPreviews, formulaType, formula },
    } = useContext(EditorContext);

    const sampleQuantity = useMemo(() => {
        if (isCoverPreview) {
            return previews?.length;
        }
        const samplePreview = showTestPreview ? testPreviews : previews;

        return samplePreview?.length
            ? Math.min(previewMessagesDefaultCount, samplePreview.length)
            : previewMessagesDefaultCount;
    }, [previews, isCoverPreview, showTestPreview, testPreviews]);

    const randomSample = useMemo(
        () => random(showTestPreview ? testPreviews : previews, sampleQuantity),
        [previews, sampleQuantity, showTestPreview, testPreviews],
    );

    const formulaHasVariant: boolean = useMemo(() => {
        if (formula && formula?.structure) {
            return !!formula.structure.find((node) => {
                if (
                    Element.isElement(node) &&
                    !Text.isText(node) &&
                    node.kind !== ElementKind.dataset &&
                    node.kind !== ElementKind.button
                ) {
                    return node.children.find((child) => {
                        return (
                            !Text.isText(child) &&
                            child.kind === ElementKind.variant
                        );
                    });
                }
                return false;
            });
        } else {
            return false;
        }
    }, [formula]);

    if (!formulaType) return null;

    const DynamicPreviewComponent =
        FormulaTypesData[formulaType].previewComponent;

    return (
        <Container className={className}>
            <DynamicPreviewComponent
                randomSample={randomSample}
                formulaHasVariant={formulaHasVariant}
                isCoverPreview={isCoverPreview}
            />
        </Container>
    );
};

export default PhoneView;

const Container = styled.div({
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
});
